import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiClock } = FiIcons;

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section id="home" className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          {/* Hero Text Content */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/2 mb-8 lg:mb-0 order-1 lg:order-1"
          >
            <div className="mb-4">
              <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                {t('hero.badge')}
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {t('hero.title')}
            </h1>

            {/* Mobile Animation */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="w-full lg:hidden mb-8 mt-2"
            >
              <div className="w-full flex justify-center items-center">
                <div className="relative w-64 h-[450px] scale-[0.85] mx-auto">
                  <div className="relative w-64 h-[450px] bg-gray-800 rounded-[36px] p-3 shadow-2xl mx-auto">
                    <div className="w-full h-full bg-gray-100 rounded-[30px] overflow-hidden relative">
                      <div className="w-full h-16 bg-gray-200 flex items-center px-4">
                        <div className="w-8 h-8 rounded-md bg-blue-500"></div>
                        <div className="ml-3">
                          <div className="h-3 w-24 bg-gray-400 rounded"></div>
                          <div className="h-2 w-16 bg-gray-300 rounded mt-1"></div>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="h-4 w-full bg-gray-300 rounded mb-3"></div>
                        <div className="h-4 w-5/6 bg-gray-300 rounded mb-3"></div>
                        <div className="h-4 w-4/6 bg-gray-300 rounded mb-5"></div>
                        <div className="h-24 w-full bg-gray-200 rounded-lg mb-4"></div>
                        <div className="h-24 w-full bg-gray-200 rounded-lg"></div>
                      </div>

                      <motion.div
                        className="absolute bottom-0 left-0 right-0 bg-white shadow-lg p-4 rounded-t-lg"
                        animate={{ y: [300, 300, 0, 0, 0, 0, 300] }}
                        transition={{ duration: 6, times: [0, 0.15, 0.25, 0.65, 0.75, 0.85, 1], repeat: Infinity, ease: "easeInOut" }}
                      >
                        <div className="text-sm font-medium mb-3">{t('cookieBanner.title')}</div>
                        <div className="text-xs text-gray-600 mb-4">{t('cookieBanner.description')}</div>
                        <div className="space-y-2">
                          <motion.button className="w-full bg-blue-500 text-white py-2 rounded-md text-sm font-medium relative overflow-hidden">
                            {t('cookieBanner.acceptAll')}
                            <motion.div
                              className="absolute inset-0 bg-white opacity-0"
                              animate={{ opacity: [0, 0, 0, 0.3, 0, 0, 0] }}
                              transition={{ duration: 6, times: [0, 0.35, 0.4, 0.45, 0.5, 0.85, 1], repeat: Infinity }}
                            />
                          </motion.button>
                          <button className="w-full border border-gray-300 py-2 rounded-md text-sm text-gray-700">
                            {t('cookieBanner.customize')}
                          </button>
                        </div>
                      </motion.div>
                    </div>
                  </div>

                  <motion.div
                    className="absolute bottom-[165px] left-1/2 -translate-x-1/2 pointer-events-none"
                    animate={{ scale: [0, 0, 0, 2, 0, 0, 0], opacity: [0, 0, 0, 0.5, 0, 0, 0] }}
                    transition={{ duration: 6, times: [0, 0.38, 0.4, 0.45, 0.5, 0.85, 1], repeat: Infinity }}
                  >
                    <div className="w-10 h-10 border-2 border-blue-400 rounded-full" />
                  </motion.div>

                  <motion.div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
                    animate={{ scale: [0, 0, 0, 0, 0, 1.2, 1, 0], opacity: [0, 0, 0, 0, 0, 1, 1, 0] }}
                    transition={{ duration: 6, times: [0, 0.45, 0.5, 0.52, 0.55, 0.6, 0.75, 0.85], repeat: Infinity }}
                  >
                    <div className="bg-green-500 text-white rounded-full p-5">
                      <SafeIcon icon={FiCheck} className="text-3xl" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <motion.a
                href="https://rebrand.ly/cookiepal-sign-5a33b3"
                rel="nofollow noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 transition-all shadow-lg text-center"
              >
                {t('hero.cta')}
              </motion.a>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-green-500" />
                <span>{t('hero.noCreditCard')}</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="text-green-500" />
                <span>{t('hero.setupTime')}</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-green-500" />
                <span>{t('hero.consentMode')}</span>
              </div>
            </div>
          </motion.div>

          {/* Desktop Animation */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:w-1/2 lg:pl-12 hidden lg:block order-2 lg:order-2"
          >
            <div className="w-full relative overflow-hidden">
              <motion.div
                className="mx-auto"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative w-64 h-[450px] bg-gray-800 rounded-[36px] p-3 shadow-2xl mx-auto">
                  <div className="w-full h-full bg-gray-100 rounded-[30px] overflow-hidden relative">
                    <div className="w-full h-16 bg-gray-200 flex items-center px-4">
                      <div className="w-8 h-8 rounded-md bg-blue-500"></div>
                      <div className="ml-3">
                        <div className="h-3 w-24 bg-gray-400 rounded"></div>
                        <div className="h-2 w-16 bg-gray-300 rounded mt-1"></div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="h-4 w-full bg-gray-300 rounded mb-3"></div>
                      <div className="h-4 w-5/6 bg-gray-300 rounded mb-3"></div>
                      <div className="h-4 w-4/6 bg-gray-300 rounded mb-5"></div>
                      <div className="h-24 w-full bg-gray-200 rounded-lg mb-4"></div>
                      <div className="h-24 w-full bg-gray-200 rounded-lg"></div>
                    </div>

                    <motion.div
                      className="absolute bottom-0 left-0 right-0 bg-white shadow-lg p-4 rounded-t-lg"
                      animate={{ y: [300, 300, 0, 0, 0, 0, 300] }}
                      transition={{ duration: 6, times: [0, 0.15, 0.25, 0.65, 0.75, 0.85, 1], repeat: Infinity, ease: "easeInOut" }}
                    >
                      <div className="text-sm font-medium mb-3">{t('cookieBanner.title')}</div>
                      <div className="text-xs text-gray-600 mb-4">{t('cookieBanner.description')}</div>
                      <div className="space-y-2">
                        <motion.button
                          className="w-full bg-blue-500 text-white py-2 rounded-md text-sm font-medium relative overflow-hidden"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {t('cookieBanner.acceptAll')}
                          <motion.div
                            className="absolute inset-0 bg-white opacity-0"
                            animate={{ opacity: [0, 0, 0, 0.3, 0, 0, 0] }}
                            transition={{ duration: 6, times: [0, 0.35, 0.4, 0.45, 0.5, 0.85, 1], repeat: Infinity }}
                          />
                        </motion.button>
                        <button className="w-full border border-gray-300 py-2 rounded-md text-sm text-gray-700">
                          {t('cookieBanner.customize')}
                        </button>
                        <button className="w-full border border-gray-300 py-2 rounded-md text-sm text-gray-700">
                          {t('cookieBanner.deny')}
                        </button>
                      </div>
                    </motion.div>
                  </div>
                </div>

                <motion.div
                  className="absolute bottom-[165px] left-1/2 -translate-x-1/2 pointer-events-none"
                  animate={{ scale: [0, 0, 0, 2, 0, 0, 0], opacity: [0, 0, 0, 0.5, 0, 0, 0] }}
                  transition={{ duration: 6, times: [0, 0.38, 0.4, 0.45, 0.5, 0.85, 1], repeat: Infinity }}
                >
                  <div className="w-10 h-10 border-2 border-blue-400 rounded-full" />
                </motion.div>

                <motion.div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
                  animate={{ scale: [0, 0, 0, 0, 0, 1.2, 1, 0], opacity: [0, 0, 0, 0, 0, 1, 1, 0] }}
                  transition={{ duration: 6, times: [0, 0.45, 0.5, 0.52, 0.55, 0.6, 0.75, 0.85], repeat: Infinity }}
                >
                  <div className="bg-green-500 text-white rounded-full p-5">
                    <SafeIcon icon={FiCheck} className="text-3xl" />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;